import React, { createContext, useContext } from 'react'
import { Box, Tooltip, Typography } from '@mui/material'

/**
 * Widget-level filter signposting (#1229 C6/C7, Matt 3 Sep).
 *
 * A multi-job dashboard is "more about signposting than architecture": a
 * reader must be able to tell, at every widget, which job it draws from and
 * which filters are on it. Each widget shows one caption line, in this
 * order: the job's DISPLAY name (never the job name), the stored page filter
 * for that job, then the viewer's quick filter for that job — the quick
 * filter is appended after the page filter, which is exactly how it is
 * applied (see applyQuickFilters).
 *
 * The page that renders the layout provides the per-job facts once through
 * this context; widgets look up their own job. Behaviour (what filter a
 * widget actually runs under) still travels through the `globalFilter` /
 * `jobFilters` props — this context is display only.
 */
export interface FilterSignpost {
  /** The job's display name. */
  jobLabel: string
  /** The stored filter applied to this job on the current page. */
  pageFilter: string
  /** The viewer-local quick filter for this job. */
  quickFilter: string
}

export interface FilterSignpostValue {
  /** Multi-job dashboards always show the job; single-job ones show the line only when a filter is on. */
  multiJob: boolean
  /** Widgets that carry no job of their own belong to the primary job. */
  primaryJob: string
  byJob: Record<string, FilterSignpost>
}

const FilterSignpostContext = createContext<FilterSignpostValue | null>(null)

export const FilterSignpostProvider = FilterSignpostContext.Provider

export interface FilterSignpostLineProps extends FilterSignpost {
  showJob: boolean
}

/**
 * Resolve the signpost line for one widget. Null means "render nothing":
 * no provider (surfaces that do not signpost, e.g. the share-link viewer),
 * an unknown job, or a single-job dashboard with no filter on.
 */
export function useFilterSignpost(widgetJob: string): FilterSignpostLineProps | null {
  return useFilterSignposts([widgetJob])[0] ?? null
}

/**
 * The signpost lines for a widget that draws from several jobs — a Gen AI
 * card whose tables come from more than one job (#1810): one line per
 * distinct job, in the order given, by the same rules as useFilterSignpost.
 */
export function useFilterSignposts(widgetJobs: string[]): FilterSignpostLineProps[] {
  const value = useContext(FilterSignpostContext)
  if (!value) return []
  const seen = new Set<string>()
  const lines: FilterSignpostLineProps[] = []
  for (const widgetJob of widgetJobs) {
    const job = widgetJob || value.primaryJob
    if (seen.has(job)) continue
    seen.add(job)
    const entry = value.byJob[job]
    if (!entry) continue
    if (!value.multiJob && !entry.pageFilter && !entry.quickFilter) continue
    lines.push({ ...entry, showJob: value.multiJob })
  }
  return lines
}

export const FilterSignpostLine: React.FC<FilterSignpostLineProps> = ({ jobLabel, pageFilter, quickFilter, showJob }) => {
  const parts: { key: string; label: string; value: string; color: string }[] = []
  if (showJob) parts.push({ key: 'job', label: 'Job', value: jobLabel, color: 'text.secondary' })
  // Page filter blue (the edit colour), quick filter orange ("be aware") —
  // the same two colours the header chips use, so the eye can match them.
  if (pageFilter) parts.push({ key: 'page', label: 'Page filter', value: pageFilter, color: 'info.main' })
  if (quickFilter) parts.push({ key: 'quick', label: 'Quick filter', value: quickFilter, color: 'warning.main' })
  if (parts.length === 0) return null

  const plain = parts.map((p) => `${p.label}: ${p.value}`).join(' · ')
  return (
    <Tooltip title={plain}>
      <Typography
        variant="caption"
        component="div"
        data-testid="filter-signpost"
        noWrap
        sx={{ minWidth: 0, color: 'text.secondary' }}
      >
        {parts.map((p, i) => (
          <Box component="span" key={p.key}>
            {i > 0 && ' · '}
            {p.label}: <Box component="span" sx={{ color: p.color }}>{p.value}</Box>
          </Box>
        ))}
      </Typography>
    </Tooltip>
  )
}

export default FilterSignpostLine
